import { useState, useCallback, useMemo } from 'react';
import { toast } from "sonner";

export type DiscriminationType = 'direct' | 'indirect' | 'harassment' | 'victimisation';
export type ProtectedCharacteristic = 
  | 'age' 
  | 'disability' 
  | 'genderReassignment' 
  | 'marriageAndCivilPartnership' 
  | 'pregnancyAndMaternity' 
  | 'race' 
  | 'religionOrBelief' 
  | 'sex' 
  | 'sexualOrientation';

export type DisabilityDiscriminationType = 
  | 'directDisability'  // Changed from 'direct' to 'directDisability'
  | 'discriminationArisingFromDisability'
  | 'failureToMakeReasonableAdjustments';

export type FormStep = 'personal' | 'protectedCharacteristics' | 'discriminationType' | 'supportingDocuments' | 'review';

export interface FormFile {
  name: string;
  type: string;
  size: number;
  file: File; // Store the actual File object instead of Base64 content
  extractedText?: string; // Add extracted text property
}

export interface KeyEvent {
  id: string;
  date: string;
  description: string;
}

export interface Person {
  id: string;
  name: string;
  role: string;
  involvement: string;
}

export interface Witness {
  id: string;
  name: string;
  role: string;
}

export interface EventsAndPeople {
  keyEvents: KeyEvent[];
  peopleInvolved: Person[];
  witnesses: Witness[];
}

export interface FormData {
  // Personal Information
  name: string;
  email: string;
  telephone: string;
  
  // Protected Characteristics
  protectedCharacteristics: ProtectedCharacteristic[];
  
  // Disability Discrimination Types (only if disability is selected)
  disabilityDiscriminationTypes: DisabilityDiscriminationType[];
  discriminationArisingFromDisabilityDetails: string;
  failureToMakeReasonableAdjustmentsDetails: string;
  
  // Discrimination Types
  discriminationTypes: DiscriminationType[];
  
  // Discrimination Details (by type)
  directDiscriminationDetails: string;
  indirectDiscriminationDetails: string;
  harassmentDetails: string;
  victimisationDetails: string;
  
  // Disability-specific direct discrimination details
  directDisabilityDiscriminationDetails: string;
  
  // Events and people data organized by discrimination type
  directDiscriminationEventsAndPeople: EventsAndPeople;
  indirectDiscriminationEventsAndPeople: EventsAndPeople;
  harassmentEventsAndPeople: EventsAndPeople;
  victimisationEventsAndPeople: EventsAndPeople;
  
  // Disability discrimination events and people
  directDisabilityEventsAndPeople: EventsAndPeople;
  discriminationArisingFromDisabilityEventsAndPeople: EventsAndPeople;
  failureToMakeReasonableAdjustmentsEventsAndPeople: EventsAndPeople;
  
  // File uploads
  uploadedFiles: FormFile[];
}

const emptyEventsAndPeople: EventsAndPeople = {
  keyEvents: [],
  peopleInvolved: [],
  witnesses: []
};

const initialFormData: FormData = {
  name: '',
  email: '',
  telephone: '',
  protectedCharacteristics: [],
  disabilityDiscriminationTypes: [],
  discriminationArisingFromDisabilityDetails: '',
  failureToMakeReasonableAdjustmentsDetails: '',
  discriminationTypes: [],
  directDiscriminationDetails: '',
  indirectDiscriminationDetails: '',
  harassmentDetails: '',
  victimisationDetails: '',
  directDisabilityDiscriminationDetails: '',
  uploadedFiles: [],
  
  // Initialize events and people arrays
  directDiscriminationEventsAndPeople: { ...emptyEventsAndPeople },
  indirectDiscriminationEventsAndPeople: { ...emptyEventsAndPeople },
  harassmentEventsAndPeople: { ...emptyEventsAndPeople },
  victimisationEventsAndPeople: { ...emptyEventsAndPeople },
  
  // Disability discrimination events and people
  directDisabilityEventsAndPeople: { ...emptyEventsAndPeople },
  discriminationArisingFromDisabilityEventsAndPeople: { ...emptyEventsAndPeople },
  failureToMakeReasonableAdjustmentsEventsAndPeople: { ...emptyEventsAndPeople }
};

// Predefined webhook URL, not visible or editable by users
const WEBHOOK_URL = "https://hook.eu2.make.com/61ow63fwk6u4ewwofdwvi1ei0pmfr5zv";

export function useFormState() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [currentStep, setCurrentStep] = useState<FormStep>('personal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [deepgramApiKey, setDeepgramApiKey] = useState<string>('');

  // Handle form field changes
  const updateFormData = useCallback((field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Update multiple fields at once
  const updateMultipleFields = useCallback((updates: Partial<FormData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Validate current step
  const validateCurrentStep = useCallback((): boolean => {
    switch (currentStep) {
      case 'personal':
        return !!formData.name && !!formData.email && !!formData.telephone;

      case 'protectedCharacteristics':
        return formData.protectedCharacteristics.length > 0;
        
      case 'supportingDocuments':
        // No validation requirements for supporting documents, it's optional
        return true;

      case 'discriminationType': {
        const hasDisability = formData.protectedCharacteristics.includes('disability');
        const hasOtherCharacteristics = formData.protectedCharacteristics.length > 1;
        
        // If only disability is selected
        if (hasDisability && !hasOtherCharacteristics) {
          // Must have at least one disability discrimination type
          const hasDisabilityType = formData.disabilityDiscriminationTypes.length > 0;
          
          // Check if direct disability discrimination details are provided if selected
          const directDetailsComplete = !formData.disabilityDiscriminationTypes.includes('directDisability') || 
            (formData.disabilityDiscriminationTypes.includes('directDisability') && 
             !!formData.directDisabilityDiscriminationDetails.trim());
          
          // Check if details for selected disability types are provided
          const disabilityDetailsComplete = formData.disabilityDiscriminationTypes.every(type => {
            if (type === 'discriminationArisingFromDisability') {
              return !!formData.discriminationArisingFromDisabilityDetails.trim();
            }
            if (type === 'failureToMakeReasonableAdjustments') {
              return !!formData.failureToMakeReasonableAdjustmentsDetails.trim();
            }
            return true;
          });
          
          // Now also check if at least one key event is added for each selected discrimination type
          const hasKeyEvents = formData.disabilityDiscriminationTypes.every(type => {
            if (type === 'directDisability') {
              return formData.directDisabilityEventsAndPeople.keyEvents.length > 0 &&
                formData.directDisabilityEventsAndPeople.keyEvents.every(
                  event => !!event.date && !!event.description
                );
            }
            if (type === 'discriminationArisingFromDisability') {
              return formData.discriminationArisingFromDisabilityEventsAndPeople.keyEvents.length > 0 &&
                formData.discriminationArisingFromDisabilityEventsAndPeople.keyEvents.every(
                  event => !!event.date && !!event.description
                );
            }
            if (type === 'failureToMakeReasonableAdjustments') {
              return formData.failureToMakeReasonableAdjustmentsEventsAndPeople.keyEvents.length > 0 &&
                formData.failureToMakeReasonableAdjustmentsEventsAndPeople.keyEvents.every(
                  event => !!event.date && !!event.description
                );
            }
            return true;
          });
          
          return hasDisabilityType && directDetailsComplete && disabilityDetailsComplete && hasKeyEvents;
        }
        
        // For all other cases (including when disability is one of multiple selected characteristics)
        const hasDiscriminationType = formData.discriminationTypes.length > 0;
        
        // Check if details are provided for each selected discrimination type
        const discriminationDetailsComplete = formData.discriminationTypes.every(type => {
          if (type === 'direct') return !!formData.directDiscriminationDetails.trim();
          if (type === 'indirect') return !!formData.indirectDiscriminationDetails.trim();
          if (type === 'harassment') return !!formData.harassmentDetails.trim();
          if (type === 'victimisation') return !!formData.victimisationDetails.trim();
          return true;
        });
        
        // Now also check if at least one key event is added for each selected discrimination type
        const hasKeyEvents = formData.discriminationTypes.every(type => {
          if (type === 'direct') {
            return formData.directDiscriminationEventsAndPeople.keyEvents.length > 0 &&
              formData.directDiscriminationEventsAndPeople.keyEvents.every(
                event => !!event.date && !!event.description
              );
          }
          if (type === 'indirect') {
            return formData.indirectDiscriminationEventsAndPeople.keyEvents.length > 0 &&
              formData.indirectDiscriminationEventsAndPeople.keyEvents.every(
                event => !!event.date && !!event.description
              );
          }
          if (type === 'harassment') {
            return formData.harassmentEventsAndPeople.keyEvents.length > 0 &&
              formData.harassmentEventsAndPeople.keyEvents.every(
                event => !!event.date && !!event.description
              );
          }
          if (type === 'victimisation') {
            return formData.victimisationEventsAndPeople.keyEvents.length > 0 &&
              formData.victimisationEventsAndPeople.keyEvents.every(
                event => !!event.date && !!event.description
              );
          }
          return true;
        });
        
        return hasDiscriminationType && discriminationDetailsComplete && hasKeyEvents;
      }

      case 'review':
        return true; // All required fields already validated
        
      default:
        return false;
    }
  }, [currentStep, formData]);

  // Get the currently active events and people section based on selected discrimination types
  const getActiveEventsAndPeople = useCallback((): EventsAndPeople | null => {
    // First check for disability discrimination types
    if (formData.protectedCharacteristics.includes('disability') && 
        formData.disabilityDiscriminationTypes.length > 0) {
      // Pick the first selected disability discrimination type
      if (formData.disabilityDiscriminationTypes.includes('directDisability')) {
        return formData.directDisabilityEventsAndPeople;
      }
      if (formData.disabilityDiscriminationTypes.includes('discriminationArisingFromDisability')) {
        return formData.discriminationArisingFromDisabilityEventsAndPeople;
      }
      if (formData.disabilityDiscriminationTypes.includes('failureToMakeReasonableAdjustments')) {
        return formData.failureToMakeReasonableAdjustmentsEventsAndPeople;
      }
    }
    
    // Then check standard discrimination types
    if (formData.discriminationTypes.length > 0) {
      // Pick the first selected standard discrimination type
      if (formData.discriminationTypes.includes('direct')) {
        return formData.directDiscriminationEventsAndPeople;
      }
      if (formData.discriminationTypes.includes('indirect')) {
        return formData.indirectDiscriminationEventsAndPeople;
      }
      if (formData.discriminationTypes.includes('harassment')) {
        return formData.harassmentEventsAndPeople;
      }
      if (formData.discriminationTypes.includes('victimisation')) {
        return formData.victimisationEventsAndPeople;
      }
    }
    
    return null;
  }, [formData]);

  // Handle next step navigation
  const goToNextStep = useCallback(() => {
    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      // Check if it's a key events validation issue
      if (currentStep === 'discriminationType') {
        const activeEventsAndPeople = getActiveEventsAndPeople();
        
        if (!activeEventsAndPeople || activeEventsAndPeople.keyEvents.length === 0) {
          toast.error("You must add at least one key event with date and description before proceeding.");
          return;
        }
        
        // Check if the added events are valid
        const hasInvalidEvents = activeEventsAndPeople.keyEvents.some(
          event => !event.date || !event.description
        );
        
        if (hasInvalidEvents) {
          toast.error("Please complete all required fields for each key event (date and description).");
          return;
        }
      }
      
      toast.error("Please complete all required fields before proceeding.");
      return;
    }
    
    // Updated step order: personal -> protectedCharacteristics -> discriminationType -> supportingDocuments -> review
    
    // From personal info to protected characteristics
    if (currentStep === 'personal') {
      setCurrentStep('protectedCharacteristics');
      return;
    }
    
    // From protected characteristics to discrimination types
    if (currentStep === 'protectedCharacteristics') {
      setCurrentStep('discriminationType');
      return;
    }
    
    // From discrimination types to supporting documents
    if (currentStep === 'discriminationType') {
      setCurrentStep('supportingDocuments');
      return;
    }
    
    // From supporting documents to review
    if (currentStep === 'supportingDocuments') {
      setCurrentStep('review');
      return;
    }
  }, [currentStep, validateCurrentStep, getActiveEventsAndPeople]);

  // Handle previous step navigation
  const goToPrevStep = useCallback(() => {
    // Updated navigation for the new step order
    if (currentStep === 'supportingDocuments') {
      setCurrentStep('discriminationType');
      return;
    }
    
    if (currentStep === 'discriminationType') {
      setCurrentStep('protectedCharacteristics');
      return;
    }
    
    if (currentStep === 'review') {
      setCurrentStep('supportingDocuments');
      return;
    }
    
    if (currentStep === 'protectedCharacteristics') {
      setCurrentStep('personal');
    }
  }, [currentStep]);

  // Reset form state
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep('personal');
    setIsCompleted(false);
  }, []);

  // Set Deepgram API key
  const setApiKey = useCallback((key: string) => {
    setDeepgramApiKey(key);
  }, []);

  // Add a file to the form data
  const addFile = useCallback((file: FormFile) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, file]
    }));
  }, []);

  // Remove a file from the form data
  const removeFile = useCallback((fileName: string) => {
    setFormData(prev => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter(file => file.name !== fileName)
    }));
  }, []);

  // Submit form data using FormData for multipart/form-data
  const submitForm = useCallback(async () => {
    setIsSubmitting(true);
    
    try {
      // Create FormData object for multipart/form-data submission
      const formDataToSubmit = new FormData();
      
      // Add all basic text fields
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('telephone', formData.telephone);
      formDataToSubmit.append('protectedCharacteristics', JSON.stringify(formData.protectedCharacteristics));
      formDataToSubmit.append('disabilityDiscriminationTypes', JSON.stringify(formData.disabilityDiscriminationTypes));
      formDataToSubmit.append('discriminationArisingFromDisabilityDetails', formData.discriminationArisingFromDisabilityDetails);
      formDataToSubmit.append('failureToMakeReasonableAdjustmentsDetails', formData.failureToMakeReasonableAdjustmentsDetails);
      formDataToSubmit.append('discriminationTypes', JSON.stringify(formData.discriminationTypes));
      formDataToSubmit.append('directDiscriminationDetails', formData.directDiscriminationDetails);
      formDataToSubmit.append('directDisabilityDiscriminationDetails', formData.directDisabilityDiscriminationDetails);
      formDataToSubmit.append('indirectDiscriminationDetails', formData.indirectDiscriminationDetails);
      formDataToSubmit.append('harassmentDetails', formData.harassmentDetails);
      formDataToSubmit.append('victimisationDetails', formData.victimisationDetails);
      formDataToSubmit.append('timestamp', new Date().toISOString());
      
      // Add all events and people data
      // Standard discrimination events and people
      formDataToSubmit.append('directDiscriminationEventsAndPeople', 
        JSON.stringify(formData.directDiscriminationEventsAndPeople));
      formDataToSubmit.append('indirectDiscriminationEventsAndPeople', 
        JSON.stringify(formData.indirectDiscriminationEventsAndPeople));
      formDataToSubmit.append('harassmentEventsAndPeople', 
        JSON.stringify(formData.harassmentEventsAndPeople));
      formDataToSubmit.append('victimisationEventsAndPeople', 
        JSON.stringify(formData.victimisationEventsAndPeople));
      
      // Disability discrimination events and people
      formDataToSubmit.append('directDisabilityEventsAndPeople', 
        JSON.stringify(formData.directDisabilityEventsAndPeople));
      formDataToSubmit.append('discriminationArisingFromDisabilityEventsAndPeople', 
        JSON.stringify(formData.discriminationArisingFromDisabilityEventsAndPeople));
      formDataToSubmit.append('failureToMakeReasonableAdjustmentsEventsAndPeople', 
        JSON.stringify(formData.failureToMakeReasonableAdjustmentsEventsAndPeople));
      
      // Add document information as JSON instead of actual file objects
      const documentMetadata = formData.uploadedFiles.map((fileItem, index) => ({
        name: fileItem.name,
        type: fileItem.type,
        size: fileItem.size,
        extractedText: fileItem.extractedText || "No text could be extracted from this file."
      }));
      
      // Add all document metadata as a single JSON field
      formDataToSubmit.append('documents', JSON.stringify(documentMetadata));
      
      // Include a complete form data JSON for easier processing
      const completeFormData = {
        personalInfo: {
          name: formData.name,
          email: formData.email,
          telephone: formData.telephone
        },
        protectedCharacteristics: formData.protectedCharacteristics,
        discriminationTypes: formData.discriminationTypes,
        disabilityDiscriminationTypes: formData.disabilityDiscriminationTypes,
        details: {
          directDiscrimination: formData.directDiscriminationDetails,
          indirectDiscrimination: formData.indirectDiscriminationDetails,
          harassment: formData.harassmentDetails,
          victimisation: formData.victimisationDetails,
          directDisabilityDiscrimination: formData.directDisabilityDiscriminationDetails,
          discriminationArisingFromDisability: formData.discriminationArisingFromDisabilityDetails,
          failureToMakeReasonableAdjustments: formData.failureToMakeReasonableAdjustmentsDetails
        },
        eventsAndPeople: {
          directDiscrimination: formData.directDiscriminationEventsAndPeople,
          indirectDiscrimination: formData.indirectDiscriminationEventsAndPeople,
          harassment: formData.harassmentEventsAndPeople,
          victimisation: formData.victimisationEventsAndPeople,
          directDisability: formData.directDisabilityEventsAndPeople,
          discriminationArisingFromDisability: formData.discriminationArisingFromDisabilityEventsAndPeople,
          failureToMakeReasonableAdjustments: formData.failureToMakeReasonableAdjustmentsEventsAndPeople
        },
        documents: documentMetadata,
        timestamp: new Date().toISOString()
      };
      
      // Add the complete form data as a JSON string
      formDataToSubmit.append('completeFormData', JSON.stringify(completeFormData));
      
      // Log the data being sent (for debugging)
      console.log("Submitting form data:", completeFormData);
      
      // Submit using FormData
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        body: formDataToSubmit,
        mode: 'no-cors', // This prevents CORS issues with external webhooks
      });
      
      setIsCompleted(true);
      toast.success("Form submitted successfully");
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("There was an error submitting the form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);

  // Calculate progress percentage
  const progressPercentage = useMemo(() => {
    const steps = ['personal', 'protectedCharacteristics', 'discriminationType', 'supportingDocuments', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    return Math.round((currentIndex / (steps.length - 1)) * 100);
  }, [currentStep]);

  return {
    formData,
    currentStep,
    isSubmitting,
    isCompleted,
    progressPercentage,
    deepgramApiKey,
    updateFormData,
    updateMultipleFields,
    goToNextStep,
    goToPrevStep,
    resetForm,
    submitForm,
    validateCurrentStep,
    setApiKey,
    addFile,
    removeFile,
    getActiveEventsAndPeople
  };
}
